import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Clock,
  List,
  Plus,
  Printer,
  RefreshCw,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/page-header';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBusiness } from '@/features/auth/auth.store';
import { useStaff } from '@/features/staff/staff.api';
import { env } from '@/config/env';
import {
  formatCalendarDay,
  formatCurrency,
  formatNumber,
  formatStoreDay,
  formatStoreShift,
  formatStoreStamp,
} from '@/lib/format';
import { formatStoreTime as formatStamp, monthBounds } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import { TimesheetCalendar } from './timesheet-calendar';
import { TimesheetEntrySheet } from './timesheet-entry-sheet';
import { useTimesheetTypes } from './timesheet-types.api';
import {
  dayBounds,
  payrollFor,
  useClockedIn,
  useTimesheets,
  type TimesheetDay,
  type TimesheetEntry,
} from './timesheets.api';

/**
 * Who worked when, and what that is worth.
 *
 * Three readings of one response — by day, by person, by shift — because a
 * manager checking a shift and an accountant totalling a fortnight want the
 * same hours arranged differently. Payroll is a mode rather than a page: the
 * rate and the deduction only appear once somebody says they are working on
 * pay, and then every row grows its money columns.
 */

type View = 'daily' | 'person' | 'entry';

/** The Monday and Sunday of the week we are in, as the range starts. */
function thisWeek() {
  const now = new Date();
  const day = (now.getDay() + 6) % 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - day);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const iso = (date: Date) =>
    new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
      .toISOString()
      .slice(0, 10);
  return { start: iso(monday), end: iso(sunday) };
}

/** One line of the table, whichever way the rows are being read. */
interface Row {
  id: string;
  who: string;
  /** Absent in the by-person view, which spans days. */
  date?: string;
  hours: number;
  minutes: number;
  entries: TimesheetEntry[];
  /** Only the by-shift view can open one for editing. */
  entry?: TimesheetEntry;
}

export function TimesheetsPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';
  const business = useBusiness();

  const [range, setRange] = useState(thisWeek);
  const [employeeId, setEmployeeId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [view, setView] = useState<View>('daily');
  /** How the by-shift view is drawn. The other two are always lists. */
  const [layout, setLayout] = useState<'list' | 'calendar'>('list');
  const [month, setMonth] = useState(() => range.start.slice(0, 7));
  const calendarOn = view === 'entry' && layout === 'calendar';
  // The calendar asks for its own month rather than bending the From and To
  // fields to it, so leaving the calendar finds the range as it was left.
  const shownRange = calendarOn ? monthBounds(month) : range;

  const [payrollOn, setPayrollOn] = useState(false);
  const [hourlyRate, setHourlyRate] = useState(
    Number(env.timesheetHourlyRate) || 0
  );
  const [deductionPercent, setDeductionPercent] = useState(
    Number(env.timesheetDeductionRate) || 0
  );
  /** Unpaid lunch, in hours, keyed by row id. Not stored anywhere. */
  const [lunch, setLunch] = useState<Record<string, number>>({});
  /** A month grid has nowhere to put a row's money, so payroll sits it out. */
  const payroll = payrollOn && !calendarOn;

  const [editing, setEditing] = useState<TimesheetEntry | 'new' | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const types = useTimesheetTypes();
  const staff = useStaff(business?.id ?? null);
  const clockedIn = useClockedIn(business?.id ?? null);

  const timesheets = useTimesheets(
    business
      ? {
          businessId: business.id,
          ...dayBounds(shownRange.start, shownRange.end),
          employeeId,
          timeSheetTypeId: typeId,
        }
      : null
  );

  const days = useMemo(() => {
    const all = timesheets.data ?? [];
    // The API returns a shift that is still open whatever range was asked
    // for. In a list that is one more row; on a month grid it has no cell,
    // and would be counted in a total it cannot be seen in.
    return calendarOn
      ? all.filter((day) => formatStamp(day.date, true).startsWith(month))
      : all;
  }, [timesheets.data, calendarOn, month]);
  const rows = useMemo(() => toRows(days, view), [days, view]);

  const typeName = (type: { nameEn: string | null; nameEs: string | null } | null) =>
    (isEnglish ? type?.nameEn : type?.nameEs) ?? '';

  const dateFilters: FilterField[] = [
    {
      id: 'start',
      label: t('timesheets.from'),
      type: 'date',
      value: range.start,
      onChange: (start) => setRange((current) => ({ ...current, start })),
    },
    {
      id: 'end',
      label: t('timesheets.to'),
      type: 'date',
      value: range.end,
      onChange: (end) => setRange((current) => ({ ...current, end })),
    },
  ];

  const filters: FilterField[] = [
    // The calendar is moved by its own arrows, a month at a time.
    ...(calendarOn ? [] : dateFilters),
    {
      id: 'employee',
      label: t('timesheets.employee'),
      value: employeeId,
      onChange: setEmployeeId,
      options: [
        { value: '', label: t('timesheets.anyEmployee') },
        ...(staff.data ?? [])
          .filter((person) => person.userId)
          .map((person) => ({
            value: person.userId!,
            label: person.completeName ?? person.email ?? '',
          })),
      ],
    },
    {
      id: 'type',
      label: t('timesheets.type'),
      value: typeId,
      onChange: setTypeId,
      disabled: (types.data ?? []).length < 2,
      options: [
        { value: '', label: t('timesheets.anyType') },
        ...(types.data ?? []).map((type) => ({
          value: type.id,
          label: typeName(type),
        })),
      ],
    },
  ];

  const totals = rows.reduce(
    (sum, row) => {
      const money = payrollFor(
        row.hours,
        lunch[row.id] ?? 0,
        hourlyRate,
        deductionPercent
      );
      return {
        hours: sum.hours + row.hours,
        lunch: sum.lunch + (lunch[row.id] ?? 0),
        gross: sum.gross + money.gross,
        deduction: sum.deduction + money.deduction,
        net: sum.net + money.net,
      };
    },
    { hours: 0, lunch: 0, gross: 0, deduction: 0, net: 0 }
  );

  function print() {
    const sheet = window.open('', '_blank', 'width=900,height=700');
    if (!sheet) {
      toast.error(t('timesheets.printBlocked'));
      return;
    }

    const head = [
      t('timesheets.who'),
      view !== 'person' ? t('timesheets.day') : null,
      view === 'entry' ? t('timesheets.startTime') : null,
      view === 'entry' ? t('timesheets.endTime') : null,
      t('timesheets.hours'),
      payroll ? t('timesheets.lunch') : null,
      payroll ? t('timesheets.gross') : null,
      payroll ? t('timesheets.deduction') : null,
      payroll ? t('timesheets.net') : null,
    ].filter(Boolean) as string[];

    const body = rows
      .map((row) => {
        const money = payrollFor(
          row.hours,
          lunch[row.id] ?? 0,
          hourlyRate,
          deductionPercent
        );
        return [
          row.who,
          view !== 'person' ? formatStoreDay(row.date) : null,
          view === 'entry' ? formatStoreStamp(row.entry?.startTime) : null,
          view === 'entry' ? formatStoreStamp(row.entry?.endTime) : null,
          formatNumber(row.hours),
          payroll ? formatNumber(lunch[row.id] ?? 0) : null,
          payroll ? formatCurrency(money.gross) : null,
          payroll ? formatCurrency(money.deduction) : null,
          payroll ? formatCurrency(money.net) : null,
        ].filter((cell) => cell !== null) as string[];
      })
      .map((cells) => `<tr>${cells.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`)
      .join('');

    // Written as a whole document and printed on load: copying a node's HTML
    // into a blank window prints unstyled, and often blank.
    sheet.document.write(`<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(t('timesheets.title'))}</title>
<style>
  body { font-family: system-ui, sans-serif; padding: 24px; color: #111; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  p { color: #555; font-size: 12px; margin: 0 0 16px; }
  table { border-collapse: collapse; width: 100%; font-size: 12px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
  th { background: #f5f5f5; }
</style></head>
<body>
  <h1>${esc(t('timesheets.title'))}</h1>
  <p>${esc(business?.name ?? '')} — ${esc(formatCalendarDay(shownRange.start))} → ${esc(formatCalendarDay(shownRange.end))}</p>
  <table>
    <thead><tr>${head.map((h) => `<th>${esc(h)}</th>`).join('')}</tr></thead>
    <tbody>${body}</tbody>
  </table>
  <script>
    window.onload = function () { window.focus(); window.print(); window.close(); };
  ${'<'}/script>
</body></html>`);
    sheet.document.close();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('timesheets.title')}
        description={business?.name ?? t('timesheets.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={rows.length === 0}
              onClick={print}
            >
              <Printer />
              <span className="hidden sm:inline">{t('timesheets.print')}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={timesheets.isFetching}
              onClick={() => void timesheets.refetch()}
            >
              <RefreshCw
                className={timesheets.isFetching ? 'animate-spin' : ''}
              />
              <span className="hidden sm:inline">{t('timesheets.refresh')}</span>
            </Button>
            <Button
              size="sm"
              disabled={!business}
              onClick={() => setEditing('new')}
            >
              <Plus />
              <span className="hidden sm:inline">
                {t('timesheets.newEntry')}
              </span>
            </Button>
          </>
        }
      />

      <Tabs defaultValue="sheets">
        <TabsList>
          <TabsTrigger value="sheets">{t('timesheets.title')}</TabsTrigger>
          <TabsTrigger value="clocked">
            {t('timesheets.clockedIn')}
            {(clockedIn.data ?? []).length > 0 && (
              <Badge variant="secondary">{clockedIn.data!.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------- timesheets */}
        <TabsContent value="sheets" className="space-y-4">
          <FilterBar
            busy={timesheets.isFetching}
            fields={filters}
            summary={
              <span>
                {t('timesheets.totalHours')}{' '}
                <strong className="tabular-nums">
                  {formatNumber(totals.hours)}
                </strong>
              </span>
            }
          />

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            {/* Three readings of the same rows. */}
            <div className="bg-muted flex rounded-lg p-1">
              {(['daily', 'person', 'entry'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setView(option)}
                  className={cn(
                    'flex-1 rounded-md px-3 py-1.5 text-sm transition-colors',
                    view === option
                      ? 'bg-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {t(`timesheets.view_${option}`)}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between gap-4">
              {view === 'entry' && (
                <div className="bg-muted flex rounded-lg p-1">
                  {(['list', 'calendar'] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={layout === option}
                      onClick={() => setLayout(option)}
                      className={cn(
                        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                        layout === option
                          ? 'bg-background shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {option === 'list' ? (
                        <List className="size-4" />
                      ) : (
                        <CalendarDays className="size-4" />
                      )}
                      {t(`timesheets.layout_${option}`)}
                    </button>
                  ))}
                </div>
              )}

              {!calendarOn && (
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={payrollOn} onCheckedChange={setPayrollOn} />
                  {t('timesheets.forPayroll')}
                </label>
              )}
            </div>
          </div>

          {payroll && (
            <div className="bg-muted/40 grid gap-3 rounded-xl border p-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t('timesheets.hourlyRate')}</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={hourlyRate}
                  onChange={(event) => setHourlyRate(Number(event.target.value))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t('timesheets.deductionPercent')}</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={deductionPercent}
                  onChange={(event) =>
                    setDeductionPercent(Number(event.target.value))
                  }
                />
              </div>
              <p className="text-muted-foreground text-xs sm:col-span-2">
                {t('timesheets.payrollHint')}
              </p>
            </div>
          )}

          {/* `isLoading`: with no store the query never runs, and a pending
              skeleton would never go away. */}
          {timesheets.isLoading && !calendarOn && (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-16 rounded-xl" />
              ))}
            </div>
          )}

          {timesheets.isError && (
            <Empty>{t('timesheets.loadFailed')}</Empty>
          )}

          {/* The calendar keeps its grid through a load and through an empty
              month: the arrows live on it, and a month nobody worked is still
              a month to page past. */}
          {calendarOn && !timesheets.isError && (
            <TimesheetCalendar
              month={month}
              onMonthChange={setMonth}
              days={days}
              busy={timesheets.isFetching}
              typeName={typeName}
              onOpen={setEditing}
            />
          )}

          {timesheets.isSuccess && rows.length === 0 && !calendarOn && (
            <Empty busy={timesheets.isFetching}>
              {t('timesheets.noEntries')}
            </Empty>
          )}

          {rows.length > 0 && !calendarOn && (
            <div className="relative divide-y rounded-xl border">
              <BusyOverlay show={timesheets.isFetching} />
              {rows.map((row) => {
                const money = payrollFor(
                  row.hours,
                  lunch[row.id] ?? 0,
                  hourlyRate,
                  deductionPercent
                );
                const canExpand = view !== 'entry' && row.entries.length > 0;
                const isOpen = expanded.has(row.id);

                return (
                  <div key={row.id} className="p-3">
                    <div className="flex items-start gap-3">
                      {canExpand ? (
                        <button
                          type="button"
                          aria-label={t('timesheets.toggleEntries')}
                          className="text-muted-foreground hover:text-foreground mt-0.5 shrink-0"
                          onClick={() =>
                            setExpanded((current) => {
                              const next = new Set(current);
                              if (next.has(row.id)) next.delete(row.id);
                              else next.add(row.id);
                              return next;
                            })
                          }
                        >
                          {isOpen ? (
                            <ChevronDown className="size-4" />
                          ) : (
                            <ChevronRight className="size-4" />
                          )}
                        </button>
                      ) : (
                        <span className="w-4 shrink-0" />
                      )}

                      <button
                        type="button"
                        disabled={!row.entry}
                        onClick={() => row.entry && setEditing(row.entry)}
                        className="min-w-0 flex-1 text-left enabled:hover:underline disabled:cursor-default"
                      >
                        <p className="truncate text-sm font-medium">{row.who}</p>
                        <p className="text-muted-foreground truncate text-xs">
                          {view === 'entry'
                            ? formatStoreShift(
                                row.entry?.startTime,
                                row.entry?.endTime,
                                t('timesheets.stillOpen')
                              )
                            : view === 'daily'
                              ? formatStoreDay(row.date)
                              : t('timesheets.nShifts', {
                                  count: row.entries.length,
                                })}
                        </p>
                        {view === 'entry' && row.entry?.comment && (
                          <p className="text-muted-foreground truncate text-xs">
                            {row.entry.comment}
                          </p>
                        )}
                        {view === 'entry' && row.entry?.type && (
                          <Badge variant="secondary" className="mt-1">
                            {typeName(row.entry.type)}
                          </Badge>
                        )}
                      </button>

                      <div className="shrink-0 text-right">
                        <p className="font-semibold tabular-nums">
                          <Clock className="text-muted-foreground mr-1 inline size-3" />
                          {formatNumber(row.hours)}
                        </p>
                        <p className="text-muted-foreground text-xs tabular-nums">
                          {formatNumber(row.minutes)} {t('timesheets.minutes')}
                        </p>
                      </div>
                    </div>

                    {/* Part of the record, not a second row: no shading and
                        no rule, indented to sit under the name. */}
                    {payroll && (
                      <div className="mt-2 grid grid-cols-2 items-end gap-x-4 gap-y-2 pl-7 sm:grid-cols-4">
                        <div className="space-y-1">
                          <Label className="text-muted-foreground text-xs font-normal">
                            {t('timesheets.lunch')}
                          </Label>
                          <Input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="0.25"
                            className="h-8"
                            value={lunch[row.id] ?? 0}
                            onChange={(event) =>
                              setLunch((current) => ({
                                ...current,
                                [row.id]: Number(event.target.value),
                              }))
                            }
                          />
                        </div>
                        <Money
                          label={t('timesheets.gross')}
                          value={money.gross}
                        />
                        <Money
                          label={t('timesheets.deduction')}
                          value={money.deduction}
                        />
                        <Money
                          label={t('timesheets.net')}
                          value={money.net}
                          strong
                        />
                      </div>
                    )}

                    {canExpand && isOpen && (
                      <div className="bg-muted/20 mt-2 divide-y rounded-lg border">
                        {row.entries.map((entry) => (
                          <button
                            key={entry.id}
                            type="button"
                            onClick={() => setEditing(entry)}
                            className="hover:bg-accent flex w-full items-center gap-3 py-2 pr-3 pl-10 text-left transition-colors"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs">
                                {formatStoreShift(
                                  entry.startTime,
                                  entry.endTime,
                                  t('timesheets.stillOpen'),
                                  // Under a day, the day is already said.
                                  view !== 'daily'
                                )}
                              </p>
                              {entry.comment && (
                                <p className="text-muted-foreground truncate text-xs">
                                  {entry.comment}
                                </p>
                              )}
                            </div>
                            {entry.type && (
                              <Badge variant="secondary" className="shrink-0">
                                {typeName(entry.type)}
                              </Badge>
                            )}
                            <span className="shrink-0 text-xs font-medium tabular-nums">
                              {entry.hours == null
                                ? '—'
                                : formatNumber(entry.hours)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

            </div>
          )}

          {/* Totals are their own card. Inside the list they read as one more
              record, which is the last thing a totals line should look like. */}
          {rows.length > 0 && (
            <div className="bg-muted/40 relative space-y-2 rounded-xl border p-4">
              <BusyOverlay show={timesheets.isFetching} size="sm" />
              <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                {t('timesheets.totals', { count: rows.length })}
              </p>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                <Money
                  label={t('timesheets.totalHours')}
                  value={totals.hours}
                  plain
                  strong
                />
                {payroll && (
                  <>
                    <Money
                      label={t('timesheets.lunch')}
                      value={totals.lunch}
                      plain
                    />
                    <Money
                      label={t('timesheets.gross')}
                      value={totals.gross}
                    />
                    <Money
                      label={t('timesheets.deduction')}
                      value={totals.deduction}
                    />
                    <Money
                      label={t('timesheets.net')}
                      value={totals.net}
                      strong
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ------------------------------------------------- clocked in */}
        <TabsContent value="clocked" className="space-y-4">
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              disabled={clockedIn.isFetching}
              onClick={() => void clockedIn.refetch()}
            >
              <RefreshCw
                className={clockedIn.isFetching ? 'animate-spin' : ''}
              />
              {t('timesheets.refresh')}
            </Button>
          </div>

          {clockedIn.isLoading && (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-14 rounded-xl" />
              ))}
            </div>
          )}

          {clockedIn.isSuccess && (clockedIn.data ?? []).length === 0 && (
            <Empty busy={clockedIn.isFetching}>
              {t('timesheets.nobodyClockedIn')}
            </Empty>
          )}

          {(clockedIn.data ?? []).length > 0 && (
            <div className="relative divide-y rounded-xl border">
              <BusyOverlay show={clockedIn.isFetching} />
              {clockedIn.data!.map((entry) => (
                <div key={entry.id} className="flex items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {entry.employee?.completeName}
                    </p>
                    <p className="text-muted-foreground truncate text-xs">
                      {t('timesheets.since')} {formatStoreStamp(entry.startTime)}
                    </p>
                  </div>
                  <Badge variant="success">{t('timesheets.onTheClock')}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <TimesheetEntrySheet
        entry={editing}
        businessId={business?.id ?? null}
        staff={staff.data ?? []}
        types={types.data ?? []}
        payroll={{ on: payroll, hourlyRate, deductionPercent }}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}

/** The one response, arranged three ways. */
function toRows(days: TimesheetDay[], view: View): Row[] {
  if (view === 'daily') {
    return days.map((day) => ({
      id: `${day.user?.id}-${day.date}`,
      who: day.user?.completeName ?? '',
      date: day.date,
      hours: day.totalHours,
      minutes: day.totalMinutes,
      entries: day.timesheet ?? [],
    }));
  }

  if (view === 'person') {
    const byUser = new Map<string, Row>();
    for (const day of days) {
      const id = day.user?.id ?? '';
      const row = byUser.get(id) ?? {
        id,
        who: day.user?.completeName ?? '',
        hours: 0,
        minutes: 0,
        entries: [],
      };
      row.hours += day.totalHours;
      row.minutes += day.totalMinutes;
      row.entries = [...row.entries, ...(day.timesheet ?? [])];
      byUser.set(id, row);
    }
    return [...byUser.values()];
  }

  return days.flatMap((day) =>
    (day.timesheet ?? []).map((entry) => ({
      id: entry.id,
      who: day.user?.completeName ?? '',
      date: day.date,
      hours: entry.hours ?? 0,
      minutes: entry.minutes ?? 0,
      entries: [],
      entry,
    }))
  );
}

function Money({
  label,
  value,
  plain,
  strong,
}: {
  label: string;
  value: number;
  plain?: boolean;
  /** The figure the row is read for — net pay, or the hours total. */
  strong?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground truncate text-xs">{label}</p>
      <p
        className={cn(
          'truncate tabular-nums',
          strong ? 'text-base font-semibold' : 'text-sm font-medium'
        )}
      >
        {plain ? formatNumber(value) : formatCurrency(value)}
      </p>
    </div>
  );
}

function Empty({
  children,
  busy,
}: {
  children: React.ReactNode;
  /** A refresh in flight: there are no rows to dim, so the note gets it. */
  busy?: boolean;
}) {
  return (
    <div className="relative rounded-xl">
      <BusyOverlay show={busy} />
      <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
        {children}
      </p>
    </div>
  );
}

/** Kept local: the print sheet is the only place raw HTML is assembled. */
function esc(value: string) {
  return value.replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[char] ?? char
  );
}
